import { Vehicle } from '@spike/vehicle-model';
import { VehiclesStatus, saveThunk } from '@spike/vehicles-action';
import useNonInitialEffect from '@versiondos/hooks';
import { OverFullWindow } from 'components/UI';
import { MultiStepForm } from 'components/UI/V3/Forms/MultiStepForm/MultiStepForm';
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import { useApiClientWrapper } from 'hooks';
import isEqual from 'lodash/isEqual';
import { FunctionComponent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { v4 as uuid } from 'uuid';
import styles from './EditVehicle.module.scss';
import VehicleDetailsStep from './Steps/Details/VehicleDetailsStep';

interface EditVehicleProps {
    vehicle?: Vehicle;
    onClose?: () => void;
    onSaved?: () => void;
}

export const EditVehicle: FunctionComponent<EditVehicleProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const vehiclesStatus = useSelector<RootState, VehiclesStatus>(state => state.vehicles.status);

    const [saving, setSaving] = useState(false);
    const [nameError, setNameError] = useState<string | null>(null);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    useNonInitialEffect(() => {
        switch (vehiclesStatus) {
            case VehiclesStatus.SaveSuccess:
                setSaving(false);
                setIsSuccess(true);
                break;
            case VehiclesStatus.DuplicatedName:
                setSaving(false);
                setNameError('This vehicle name already exists');
                break;
            case VehiclesStatus.Error:
                setSaving(false);
                break;
        }
    }, [vehiclesStatus]);

    // Helper functions for titles and messages
    const getTitle = () => {
        return props.vehicle ? 'Editing Vehicle' : 'Add Vehicle';
    };

    const getSuccessTitle = () => {
        return props.vehicle ? 'Vehicle Updated!' : 'Vehicle Created!';
    };

    const getSuccessMessage = () => {
        const action = props.vehicle ? 'updated' : 'created';
        return (
            <p>
                <b>{editVehicle.name}</b> has been {action} successfully.
            </p>
        );
    };

    const getSuccessButtonLabel = () => {
        return 'Done';
    };

    const newVehicle: Vehicle = {
        uuid: uuid(),
        marketplaceId: apiClientWrapper.marketplaceId,
        name: '',
        maxPets: 1,
        active: true,
        photos: []
    };

    const [editVehicle, setEditVehicle] = useState<Vehicle>(props.vehicle ?? newVehicle);

    const saveHandler = () => {
        setNameError(null);
        setSaving(true);
        dispatch(saveThunk(apiClientWrapper, editVehicle));
    };

    const closeHandler = () => {
        if (!isEqual(props.vehicle ?? newVehicle, editVehicle)) {
            setShowUnsavedModal(true);
        } else {
            props.onClose?.();
        }
    };

    // Early return for success state (Boarding pattern)
    if (isSuccess) {
        return (
            <OverFullWindow
                showMobileLogo={true}
                title={getTitle()}
                onClose={props.onSaved}
            >
                <SuccessScreen
                    title={getSuccessTitle()}
                    message={getSuccessMessage()}
                    buttonLabel={getSuccessButtonLabel()}
                    onClick={props.onSaved}
                />
            </OverFullWindow>
        );
    }

    // Normal form view
    return (
        <OverFullWindow
            title={getTitle()}
            showMobileLogo={true}
            onClose={closeHandler}
        >
            {showUnsavedModal && (
                <ExitWithoutSavingModal
                    onDiscard={() => props.onClose?.()}
                    onKeepEditing={() => setShowUnsavedModal(false)}
                />
            )}
            <div className={styles.container}>
                <MultiStepForm
                    steps={[
                        {
                            title: 'Vehicle Details',
                            component: (
                                <VehicleDetailsStep
                                    vehicle={editVehicle}
                                    nameError={nameError}
                                    onChange={setEditVehicle}
                                    onSave={saveHandler}
                                    saving={saving}
                                />
                            )
                        }
                    ]}
                    activeStep={0}
                    onEdit={() => {}}
                    className={styles.multipleStepForm}
                />
            </div>
        </OverFullWindow>
    );
};

export default EditVehicle;
